import moment from 'moment';

import { paymentErrors } from '@elab/core/utils/constants';

import { globalTypes } from '../types';
import { reduceAction } from '../helpers';

import types from './types';

export const initialState = () => ({
    methods: [],
    giftCardMethods: [],
    selectedMethod: null,
    shouldSubscribe: false,
    termsAccepted: false,
    paymentDisabled: false,
    showPayWithPoints: false,
    savedCards: [],
    saveCardUsed: false,
    selectedCard: null,
    cardHolderName: '',
    saveCard: false,
    cardSaveSuccess: false,
    iFrame: null,
    errors: {},
    loading: false,
    removeCard: false,
    payNowActive: false,
    paymentMethodSet: false,
    cartNotFound: false,
    distanceSellingContract: false,
    preliminaryInformationForm: false,
    sessionData: {
        success: false,
        clientToken: null,
        paymentCategories: null,
        authToken: null,
        deliveryLineName: null,
        lastUpd: null,
        sessionId: null,
    },
});

const reducers = (state = initialState(), { payload, type }) => {
    switch (type) {
        case globalTypes.INITIALIZE:
            return initialState();

        case types.SET_TERMS_ACCEPTED:
            return reduceAction(state, 'termsAccepted', payload);

        case types.SET_PAYMENT_METHODS:
            return reduceAction(state, 'methods', payload || []);

        case types.SET_GIFT_CARD_METHODS:
            return reduceAction(state, 'giftCardMethods', payload);

        case types.SET_SELECTED_CARD:
            return reduceAction(state, 'selectedCard', payload || null);

        case types.SET_CART_NOT_FOUND:
            return reduceAction(state, 'cartNotFound', payload);

        case types.SET_CARD_HOLDER_NAME:
            return reduceAction(state, 'cardHolderName', payload || '');

        case types.SET_SAVE_CARD_FLAG:
            return reduceAction(state, 'saveCard', payload || false);

        case types.SET_PAY_NOW_ACTIVE:
            return reduceAction(state, 'payNowActive', payload);

        case types.SET_SAVE_CARD_USED:
            return reduceAction(state, 'saveCardUsed', payload);

        case types.RESET_CARD_SELECTION:
            return {
                ...state,
                selectedCard: null,
                saveCard: false,
                cardHolderName: '',
                saveCardUsed: false,
                iFrame: null,
            };

        case types.SET_IS_PAYMENT_PAGE:
            return reduceAction(state, 'isPaymentPage', payload);

        case types.RESET_PAYMENT_PAGE_DATA: {
            const newState = { ...state, errors: {} };
            delete newState.isPaymentPage;
            return newState;
        }

        case types.SET_ORDER_API_ERROR: {
            return {
                ...state,
                showApiErrorForPlaceOrder: payload,
            };
        }

        case types.RESET_SOP_DATA:
            return {
                ...state,
                payNowActive: false,
                removeCard: false,
                removeCardNumber: '',
                removeCardType: '',
                removeCardId: '',
            };

        case types.SET_SAVED_CARDS: {
            return { ...state, savedCards: payload || [] };
        }

        case types.SET_SAVED_CARDS_AVAILABLE:
            return reduceAction(state, 'savedCardsAvailable', payload);

        case types.SET_ERROR: {
            const { name, value } = payload || {};
            return {
                ...state,
                errors: { ...state.errors, [name]: value },
            };
        }

        case types.SET_IFRAME_URL:
            return reduceAction(state, 'iFrame', payload || '');

        case types.RESET_ERRORS:
            return { ...state, errors: {} };

        case types.SET_PAYMENT_METHOD:
            return {
                ...state,
                selectedMethod: payload || null,
                paymentMethodSet: false,
                errors: {
                    ...state.errors,
                    [paymentErrors.iFrameError]: '',
                    [paymentErrors.iFrameSaveCardError]: '',
                    [paymentErrors.fetchIframeError]: '',
                },
            };

        case types.RESET_PAYMENT_METHOD:
            return {
                ...state,
                selectedMethod: payload || null,
                paymentMethodSet: false,
            };

        case types.PAYMENT_METHOD_SET:
            return reduceAction(state, 'paymentMethodSet', true);

        case types.SET_TOKENIZATION_ALLOWED:
            return reduceAction(state, 'tokenizationAllowed', !!payload);

        case types.SET_PAYMENT_AVAILABILITY:
            return reduceAction(state, 'paymentDisabled', !payload);

        case types.SET_REMOVE_CARD:
            return {
                ...state,
                removeCardNumber: payload.number,
                removeCardType: payload.type,
                removeCardId: payload.cardId,
            };

        case types.SET_REMOVE_CARD_RESET:
            return {
                ...state,
                removeCardNumber: '',
                removeCardType: '',
                removeCardId: '',
            };

        case types.SET_TERMS:
            return reduceAction(state, 'termsAccepted', payload);

        case types.SET_CARD_SAVE_SUCCESS:
            return {
                ...state,
                errors: { cardSaveSuccess: Boolean(payload.value) },
            };

        case types.TOGGLE_FIELD_VALUE:
            return reduceAction(state, payload.name, payload.value);

        case types.SET_PAY_WITH_LOYALTY_POINTS_METHOD:
            return {
                ...state,
                showPayWithPoints: payload && payload.serverResponseStatus === 200,
                methods: [
                    ...state.methods.filter(
                        ({ code }) => !code.toLowerCase().includes('paywithpoints'),
                    ),
                    {
                        ...state.methods.find(({ code }) =>
                            code.toLowerCase().includes('paywithpoints'),
                        ),
                        ...payload,
                    },
                ],
            };

        case types.SET_LOADING:
            return reduceAction(state, 'loading', payload);

        case types.SET_IS_NEW_ORDER_API_ENABLED:
            return reduceAction(state, 'isNewOrderApiEnabled', payload);

        case types.SET_PAYMENT_SESSION_AUTHORIZED:
            return reduceAction(state, 'sessionAuthorized', payload);

        case types.SET_PAYMENT_SESSION:
            return {
                ...state,
                sessionData: {
                    success: !!payload.success,
                    clientToken: payload.clientToken || null,
                    paymentCategories: payload.paymentCategories || null,
                    authToken: payload.authToken || null,
                    deliveryLineName: payload.deliveryLineName || null,
                    lastUpd: moment().toISOString() || null,
                    sessionId: payload.sessionId,
                },
            };

        case types.SET_PAYMENT_SESSION_DELIVERY_LINE:
            return {
                ...state,
                sessionData: {
                    ...state.sessionData,
                    deliveryLineName: payload || null,
                },
            };

        case types.CLEAR_PAYMENT_SESSION:
            return {
                ...state,
                sessionData: {
                    success: false,
                    clientToken: null,
                    paymentCategories: null,
                    authToken: null,
                    deliveryLineName: null,
                    lastUpd: null,
                    sessionId: null,
                },
            };
        case types.RESET_REQUIRED_CHECK:
            return {
                ...state,
                distanceSellingContract: false,
                preliminaryInformationForm: false,
            };
        default:
            return state;
    }
};

export default reducers;
